import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import emailService from '../services/emailService';

const Home = () => {
  const [stats, setStats] = useState({
    totalEmails: 0,
    analyzedEmails: 0,
    averageSentiment: 0
  });
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    // Ne faire l'appel API que si l'utilisateur est authentifié
    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated]);

  const fetchStats = async () => {
    try {
      const data = await emailService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
    }
  };


  const features = [
    {
      icon: 'bi-brain',
      title: 'Analyse IA',
      description: 'Analyse automatique du sentiment, des entités et du contenu',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'bi-lightning',
      title: 'Traitement rapide',
      description: 'Import et analyse de milliers d\'emails en quelques minutes',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: 'bi-shield-check',
      title: 'Sécurisé',
      description: 'Vos données restent privées et sécurisées',
      color: 'from-green-500 to-teal-500'
    },
    {
      icon: 'bi-graph-up',
      title: 'Analytics avancés',
      description: 'Visualisations et métriques détaillées',
      color: 'from-blue-500 to-indigo-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container-fluid py-12">

        {/* Hero Section */}
        <section className="text-center mb-16">
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center animate-pulse">
                <i className="bi bi-envelope text-white text-4xl"></i>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <i className="bi bi-sparkles text-yellow-700"></i>
              </div>
            </div>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
              Discover
            </span>
            <br />
            {/*  <span className="bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
              tout en protégeant vos données
            </span> */}
          </h1>

          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Transformez vos archives en insights précieux grâce à l'intelligence artificielle.
            Apprenez, extrayez les entités et découvrez des patterns cachés.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/chatbot')}
              className="btn btn-primary btn-lg flex items-center justify-center space-x-2 hover:scale-105 transform transition-transform"
            >
              <i className="bi bi-chat-dots"></i>
              <span>Commencer l'analyse</span>
              <i className="bi bi-arrow-right"></i>
            </button>

            <button
              onClick={() => navigate('/emails/import')}
              className="btn btn-outline-primary btn-lg flex items-center justify-center space-x-2 hover:scale-105 transform transition-transform"
            >
              <i className="bi bi-upload"></i>
              <span>Importer des emails</span>
            </button>

            <button
              onClick={() => navigate('/emails')}
              className="btn btn-secondary btn-lg flex items-center justify-center space-x-2 hover:scale-105 transform transition-transform"
            >
              <i className="bi bi-envelope"></i>
              <span>Voir les emails</span>
            </button>
          </div>
        </section>

        {/* Stats Section */}
        {(stats.totalEmails > 0) && (
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card hover:shadow-xl transition-shadow duration-300 p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="bi bi-file-text text-white text-xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-2">
                  {stats.totalEmails?.toLocaleString() || 0}
                </h3>
                <p className="text-slate-600">Emails importés</p>
              </div>

              <div className="card hover:shadow-xl transition-shadow duration-300 p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="bi bi-brain text-white text-xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-2">
                  {stats.analyzedEmails?.toLocaleString() || 0}
                </h3>
                <p className="text-slate-600">Analyses</p>
              </div>

              <div className="card hover:shadow-xl transition-shadow duration-300 p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="bi bi-graph-up text-white text-xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-2">
                  {((stats.averageSentiment || 0) * 100).toFixed(1)}%
                </h3>
                <p className="text-slate-600">Sentiment moyen</p>
              </div>
            </div>
          </section>
        )}


        {/* Features Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Pourquoi choisir Discover ?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Une suite complète d'outils d'analyse d'emails alimentée par l'IA
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card hover:shadow-xl transition-all duration-300 hover:-translate-y-1 p-6 text-center"
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${feature.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="glass rounded-2xl p-12 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Prêt à analyser vos emails ?
            </h2>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
              Découvrez les insights cachés dans votre correspondance électronique grâce à notre assistant IA avancé.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/chatbot')}
                className="btn btn-primary btn-lg flex items-center justify-center space-x-2 hover:scale-105 transform transition-transform"
              >
                <i className="bi bi-robot"></i>
                <span>Essayer l'assistant IA</span>
              </button>

              {/* Boutons Analytics et Insights visibles uniquement pour les admins */}
              {user?.role?.toLowerCase() === 'admin' && (
                <>
                  <button
                    onClick={() => navigate('/analytics')}
                    className="btn btn-outline-primary btn-lg flex items-center justify-center space-x-2 hover:scale-105 transform transition-transform"
                  >
                    <i className="bi bi-graph-up"></i>
                    <span>Voir les analytics</span>
                  </button>
                  <button
                    onClick={() => navigate('/insights')}
                    className="btn btn-outline-secondary btn-lg flex items-center justify-center space-x-2 hover:scale-105 transform transition-transform"
                  >
                    <i className="bi bi-lightbulb"></i>
                    <span>Analyses avancées</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Home; 